import { useEffect, useState, Fragment } from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  EditNote as EditNoteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';

export default function AnalyticsAdvert() {
  const { error: showError, success: showSuccess } = useNotify();

  const formatYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const today = new Date();
  const minus7 = new Date(today);
  minus7.setDate(today.getDate() - 7);

  const [dateFrom, setDateFrom] = useState(formatYmd(minus7));
  const [dateTo, setDateTo] = useState(formatYmd(today));
  const [mpSel, setMpSel] = useState(2); // Default Ozon
  const [orgSel, setOrgSel] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [placementSel, setPlacementSel] = useState('');
  const [placements, setPlacements] = useState([]);
  const [stateSel, setStateSel] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  // Маппинг статусов кампаний
  const stateMap = {
    CAMPAIGN_STATE_RUNNING: 'Активна',
    CAMPAIGN_STATE_INACTIVE: 'На паузе',
    CAMPAIGN_STATE_ARCHIVED: 'Архив',
    CAMPAIGN_STATE_FINISHED: 'Завершена',
  };
  const stateOptions = Object.entries(stateMap).map(([value, label]) => ({ value, label }));

  // Expanded states
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [campaignProducts, setCampaignProducts] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productDates, setProductDates] = useState({});

  // Note editing state
  const [noteDialog, setNoteDialog] = useState({
    open: false,
    campaign_id: null,
    sku: null,
    stat_date: null,
    note: '',
    saving: false,
  });

  // Load organizations and placements
  useEffect(() => {
    axios.get('/analytics/advert/organizations')
      .then(resp => setOrgs(resp.data.rows || []))
      .catch(() => {});
    axios.get('/analytics/advert/placements')
      .then(resp => setPlacements(resp.data.rows || []))
      .catch(() => {});
  }, []);

  // Load campaigns
  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (orgSel) params.organization = orgSel;
      if (placementSel) params.placement = placementSel;
      if (stateSel) params.state = stateSel;

      const resp = await axios.get('/analytics/advert/campaigns', { params });
      setCampaigns(resp.data.rows || []);
      setExpandedCampaigns({});
      setCampaignProducts({});
      setExpandedProducts({});
      setProductDates({});
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // Toggle campaign expansion
  const toggleCampaign = async (campaignId, organization) => {
    const key = `${campaignId}_${organization}`;
    const isExpanded = expandedCampaigns[key];

    if (isExpanded) {
      setExpandedCampaigns(prev => ({ ...prev, [key]: false }));
      return;
    }

    // Load products if not loaded
    if (!campaignProducts[key]) {
      try {
        const params = { date_from: dateFrom, date_to: dateTo, organization };
        const resp = await axios.get(`/analytics/advert/campaigns/${campaignId}/products`, { params });
        setCampaignProducts(prev => ({ ...prev, [key]: resp.data.rows || [] }));
      } catch (e) {
        showError('Ошибка загрузки продуктов');
      }
    }

    setExpandedCampaigns(prev => ({ ...prev, [key]: true }));
  };

  // Toggle product expansion
  const toggleProduct = async (campaignId, sku, organization) => {
    const key = `${campaignId}_${sku}_${organization}`;
    const isExpanded = expandedProducts[key];

    if (isExpanded) {
      setExpandedProducts(prev => ({ ...prev, [key]: false }));
      return;
    }

    // Load dates if not loaded
    if (!productDates[key]) {
      try {
        const params = { date_from: dateFrom, date_to: dateTo, organization };
        const resp = await axios.get(`/analytics/advert/campaigns/${campaignId}/products/${sku}/dates`, { params });
        setProductDates(prev => ({ ...prev, [key]: resp.data.rows || [] }));
      } catch (e) {
        showError('Ошибка загрузки статистики');
      }
    }

    setExpandedProducts(prev => ({ ...prev, [key]: true }));
  };

  // Open note dialog
  const openNoteDialog = (e, campaign_id, sku, stat_date, note) => {
    e.stopPropagation();
    setNoteDialog({ open: true, campaign_id, sku, stat_date, note: note || '', saving: false });
  };

  // Save note
  const saveNote = async () => {
    setNoteDialog(prev => ({ ...prev, saving: true }));
    try {
      await axios.put('/analytics/advert/note', {
        campaign_id: noteDialog.campaign_id,
        sku: noteDialog.sku,
        stat_date: noteDialog.stat_date,
        note: noteDialog.note,
      });

      // Update local state
      const prodKey = `${noteDialog.campaign_id}_${noteDialog.sku}_${orgSel || campaigns.find(c => c.campaign_id === noteDialog.campaign_id)?.organization}`;
      setProductDates(prev => {
        if (!prev[prodKey]) return prev;
        return {
          ...prev,
          [prodKey]: prev[prodKey].map(dt =>
            dt.stat_date === noteDialog.stat_date ? { ...dt, note: noteDialog.note } : dt
          ),
        };
      });

      showSuccess('Заметка сохранена');
      setNoteDialog({ open: false, campaign_id: null, sku: null, stat_date: null, note: '', saving: false });
    } catch (e) {
      showError(e?.response?.data?.message || 'Ошибка сохранения заметки');
      setNoteDialog(prev => ({ ...prev, saving: false }));
    }
  };

  // Format number with spaces
  const fmtNum = (v, decimals = 0) => {
    if (v == null || v === '') return '-';
    const n = Number(v);
    if (isNaN(n)) return '-';
    return n.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const fmtMoney = (v) => fmtNum(v, 2) + ' ₽';
  const fmtPct = (v) => {
    if (v == null || v === '') return '-';
    return fmtNum(v, 2) + '%';
  };

  const isCpoCampaign = (camp) => String(camp?.payment_type || '').toUpperCase() === 'CPO';
  const cpoBlankKeys = new Set([
    'views', 'clicks', 'ctr', 'to_cart', 'cart_conversion', 'avg_cpc',
    'models_count', 'models_money', 'order_conversion',
  ]);
  const paymentTypeLabel = (camp) => {
    if (camp?.adv_object_type === 'ALL_SKU_PROMO') return 'За заказ (все товары)';
    if (camp?.adv_object_type === 'SEARCH_PROMO') return 'За заказ (выбранные)';
    if (isCpoCampaign(camp)) return 'За заказ';
    if (String(camp?.payment_type || '').toUpperCase() === 'CPC') return 'За клик';
    return null;
  };
  const formatMetric = (h, row, camp) => {
    if (isCpoCampaign(camp || row) && cpoBlankKeys.has(h.key)) return '—';
    if (row[h.key] == null || row[h.key] === '') return '—';
    return h.format ? h.format(row[h.key]) : fmtNum(row[h.key]);
  };

  // Table headers
  const headers = [
    { label: 'Показы', key: 'views', align: 'right' },
    { label: 'Клики', key: 'clicks', align: 'right' },
    { label: 'CTR', key: 'ctr', align: 'right', format: fmtPct },
    { label: 'В корзину', key: 'to_cart', align: 'right' },
    { label: 'CR корзина', key: 'cart_conversion', align: 'right', format: fmtPct },
    { label: 'CPC', key: 'avg_cpc', align: 'right', format: fmtMoney },
    { label: 'Расход', key: 'money_spent', align: 'right', format: fmtMoney },
    { label: 'Заказы', key: 'orders_count', align: 'right' },
    { label: 'Выручка', key: 'orders_money', align: 'right', format: fmtMoney },
    { label: 'Заказы асс', key: 'models_count', align: 'right' },
    { label: 'Выручка асс', key: 'models_money', align: 'right', format: fmtMoney },
    { label: 'CR заказ', key: 'order_conversion', align: 'right', format: fmtPct },
    { label: 'ДРР', key: 'drr', align: 'right', format: fmtPct },
  ];

  const cellStyle = { py: 0.5, px: 1, fontSize: '0.85rem', whiteSpace: 'nowrap' };
  const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

  // ── Helpers for weekly subtotals ──
  const getMonday = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDay(); // 0=Sun … 6=Sat
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return formatYmd(d);
  };
  const getSunday = (mondayStr) => {
    const d = new Date(mondayStr + 'T00:00:00');
    d.setDate(d.getDate() + 6);
    return formatYmd(d);
  };
  const fmtShortDate = (dateStr) => {
    const p = dateStr.split('-');
    return `${p[2]}.${p[1]}`;
  };

  /** Group an array of date-rows by ISO week, compute subtotals */
  const groupDatesByWeek = (dates) => {
    if (!dates || dates.length === 0) return [];
    const weekMap = new Map();
    for (const dt of dates) {
      const mon = getMonday(dt.stat_date);
      if (!weekMap.has(mon)) weekMap.set(mon, []);
      weekMap.get(mon).push(dt);
    }
    const weeks = [];
    for (const [monday, rows] of weekMap) {
      const sunday = getSunday(monday);
      const label = `${fmtShortDate(monday)} – ${fmtShortDate(sunday)}`;
      const sum = (k) => rows.reduce((s, r) => s + Number(r[k] || 0), 0);
      const tViews = sum('views');
      const tClicks = sum('clicks');
      const tCart = sum('to_cart');
      const tSpent = sum('money_spent');
      const tOrdCnt = sum('orders_count');
      const tModCnt = sum('models_count');
      const tOrdMon = sum('orders_money');
      const tModMon = sum('models_money');
      const tRev = tOrdMon + tModMon;
      const totals = {
        views: tViews, clicks: tClicks,
        ctr: tViews > 0 ? (tClicks / tViews * 100) : 0,
        to_cart: tCart,
        cart_conversion: tClicks > 0 ? (tCart / tClicks * 100) : 0,
        avg_cpc: tClicks > 0 ? (tSpent / tClicks) : 0,
        money_spent: tSpent,
        orders_count: tOrdCnt, orders_money: tOrdMon,
        models_count: tModCnt, models_money: tModMon,
        order_conversion: tClicks > 0 ? ((tOrdCnt + tModCnt) / tClicks * 100) : 0,
        drr: tRev > 0 ? (tSpent / tRev * 100) : 0,
      };
      weeks.push({ monday, label, rows, totals });
    }
    weeks.sort((a, b) => b.monday.localeCompare(a.monday));
    return weeks;
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Рекламная аналитика
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Маркетплейс"
              value={mpSel}
              onChange={(e) => setMpSel(Number(e.target.value))}
              size="small"
              fullWidth
            >
              <MenuItem value={2}>Ozon</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <DateRangeField
              valueFrom={dateFrom}
              valueTo={dateTo}
              onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Организация"
              value={orgSel}
              onChange={(e) => setOrgSel(e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="">Все организации</MenuItem>
              {orgs.map(org => (
                <MenuItem key={org} value={org}>{org}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Тип размещения"
              value={placementSel}
              onChange={(e) => setPlacementSel(e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="">Все типы</MenuItem>
              {placements.map(p => (
                <MenuItem key={p.placement_code} value={p.placement_code}>{p.placement_name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Статус РК"
              value={stateSel}
              onChange={(e) => setStateSel(e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="">Все статусы</MenuItem>
              {stateOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              onClick={loadCampaigns}
              disabled={loading}
              fullWidth
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            >
              {loading ? 'Загрузка...' : 'Загрузить'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Campaigns Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headerStyle} style={{ width: 40 }}></TableCell>
              <TableCell sx={headerStyle}>Кампания</TableCell>
              <TableCell sx={headerStyle}>Организация</TableCell>
              {headers.map(h => (
                <TableCell key={h.key} sx={headerStyle} align={h.align}>{h.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length + 3} align="center" sx={{ py: 4 }}>
                  {loading ? 'Загрузка...' : 'Нет данных. Выберите период и нажмите "Загрузить"'}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {campaigns.map((camp) => {
                  const campKey = `${camp.campaign_id}_${camp.organization}`;
                  const isExpanded = expandedCampaigns[campKey];
                  const products = campaignProducts[campKey] || [];

                  return (
                    <Fragment key={campKey}>
                      {/* Campaign Row */}
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', bgcolor: isExpanded ? '#e3f2fd' : 'inherit' }}
                        onClick={() => toggleCampaign(camp.campaign_id, camp.organization)}
                      >
                        <TableCell sx={cellStyle}>
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {camp.campaign_title || `Кампания #${camp.campaign_id}`}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                              <Typography variant="caption" color="text.secondary">
                                ID: {camp.campaign_id}
                              </Typography>
                              {paymentTypeLabel(camp) && (
                                <Chip
                                  label={paymentTypeLabel(camp)}
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: '0.7rem',
                                    bgcolor: isCpoCampaign(camp) ? '#f3e5f5' : '#e8eaf6',
                                    color: isCpoCampaign(camp) ? '#6a1b9a' : '#283593',
                                  }}
                                />
                              )}
                              {camp.campaign_state && (
                                <Chip
                                  label={stateMap[camp.campaign_state] || camp.campaign_state}
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: '0.7rem',
                                    bgcolor: camp.campaign_state === 'CAMPAIGN_STATE_RUNNING' ? '#e8f5e9'
                                      : camp.campaign_state === 'CAMPAIGN_STATE_INACTIVE' ? '#fff3e0'
                                      : camp.campaign_state === 'CAMPAIGN_STATE_ARCHIVED' ? '#f5f5f5'
                                      : '#fce4ec',
                                    color: camp.campaign_state === 'CAMPAIGN_STATE_RUNNING' ? '#2e7d32'
                                      : camp.campaign_state === 'CAMPAIGN_STATE_INACTIVE' ? '#e65100'
                                      : 'text.secondary',
                                  }}
                                />
                              )}
                              {camp.placement && (
                                <Chip
                                  label={camp.placement}
                                  size="small"
                                  sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#e3f2fd' }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={cellStyle}>{camp.organization}</TableCell>
                        {headers.map(h => (
                          <TableCell key={h.key} sx={cellStyle} align={h.align}>
                            {formatMetric(h, camp)}
                          </TableCell>
                        ))}
                      </TableRow>

                      {/* Products Collapse */}
                      <TableRow>
                        <TableCell colSpan={headers.length + 3} sx={{ p: 0, borderBottom: isExpanded ? 'none' : undefined }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: 6, pr: 2, py: 1, bgcolor: '#fafafa' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={headerStyle} style={{ width: 40 }}></TableCell>
                                    <TableCell sx={headerStyle}>SKU</TableCell>
                                    <TableCell sx={headerStyle}>Артикул</TableCell>
                                    <TableCell sx={headerStyle} align="right">Цена</TableCell>
                                    {headers.map(h => (
                                      <TableCell key={h.key} sx={headerStyle} align={h.align}>{h.label}</TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {products.map((prod) => {
                                    const prodKey = `${camp.campaign_id}_${prod.sku}_${camp.organization}`;
                                    const isProdExpanded = expandedProducts[prodKey];
                                    const dates = productDates[prodKey] || [];

                                    return (
                                      <Fragment key={prodKey}>
                                        {/* Product Row */}
                                        <TableRow
                                          hover
                                          sx={{ cursor: 'pointer', bgcolor: isProdExpanded ? '#e8f5e9' : 'inherit' }}
                                          onClick={() => toggleProduct(camp.campaign_id, prod.sku, camp.organization)}
                                        >
                                          <TableCell sx={cellStyle}>
                                            <IconButton size="small">
                                              {isProdExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                            </IconButton>
                                          </TableCell>
                                          <TableCell sx={cellStyle}>{prod.sku}</TableCell>
                                          <TableCell sx={cellStyle}>
                                            <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {prod.offer_id}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={cellStyle} align="right">{fmtMoney(prod.price)}</TableCell>
                                          {headers.map(h => (
                                            <TableCell key={h.key} sx={cellStyle} align={h.align}>
                                              {formatMetric(h, prod, camp)}
                                            </TableCell>
                                          ))}
                                        </TableRow>

                                        {/* Dates Collapse */}
                                        <TableRow>
                                          <TableCell colSpan={headers.length + 4} sx={{ p: 0 }}>
                                            <Collapse in={isProdExpanded} timeout="auto" unmountOnExit>
                                              <Box sx={{ pl: 8, pr: 2, py: 1, bgcolor: '#f5f5f5' }}>
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell sx={headerStyle}>Дата</TableCell>
                                                      <TableCell sx={headerStyle} align="right">Цена</TableCell>
                                                      {headers.map(h => (
                                                        <TableCell key={h.key} sx={headerStyle} align={h.align}>{h.label}</TableCell>
                                                      ))}
                                                      <TableCell sx={headerStyle}>Заметка</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {groupDatesByWeek(dates).map((week) => (
                                                      <Fragment key={week.monday}>
                                                        {week.rows.map((dt, idx) => (
                                                          <TableRow key={idx}>
                                                            <TableCell sx={cellStyle}>{dt.stat_date}</TableCell>
                                                            <TableCell sx={cellStyle} align="right">{fmtMoney(dt.price)}</TableCell>
                                                            {headers.map(h => (
                                                              <TableCell key={h.key} sx={cellStyle} align={h.align}>
                                                                {formatMetric(h, dt, camp)}
                                                              </TableCell>
                                                            ))}
                                                            <TableCell sx={cellStyle}>
                                                              {isCpoCampaign(camp) ? (
                                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                                              ) : (
                                                                <Tooltip title={dt.note || 'Добавить заметку'} arrow>
                                                                  <Box
                                                                    sx={{
                                                                      display: 'flex',
                                                                      alignItems: 'center',
                                                                      cursor: 'pointer',
                                                                      '&:hover': { color: 'primary.main' },
                                                                    }}
                                                                    onClick={(e) => openNoteDialog(e, camp.campaign_id, prod.sku, dt.stat_date, dt.note)}
                                                                  >
                                                                    {dt.note ? (
                                                                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {dt.note}
                                                                      </Typography>
                                                                    ) : (
                                                                      <EditNoteIcon fontSize="small" color="disabled" />
                                                                    )}
                                                                  </Box>
                                                                </Tooltip>
                                                              )}
                                                            </TableCell>
                                                          </TableRow>
                                                        ))}
                                                        {/* Week subtotal row */}
                                                        <TableRow sx={{ bgcolor: '#fff8e1' }}>
                                                          <TableCell sx={{ ...cellStyle, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                                            {week.label}
                                                          </TableCell>
                                                          <TableCell sx={cellStyle}></TableCell>
                                                          {headers.map(h => (
                                                            <TableCell key={h.key} sx={{ ...cellStyle, fontWeight: 'bold' }} align={h.align}>
                                                              {formatMetric(h, week.totals, camp)}
                                                            </TableCell>
                                                          ))}
                                                          <TableCell sx={cellStyle}></TableCell>
                                                        </TableRow>
                                                      </Fragment>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      </Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
                {/* Итоговая строка */}
                <TableRow sx={{ bgcolor: '#e8eaf6', fontWeight: 'bold' }}>
                  <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }}>
                    Итого ({campaigns.length} кампаний)
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }}></TableCell>
                  {headers.map(h => {
                    let value;
                    if (h.key === 'ctr') {
                      const totalClicks = campaigns.reduce((s, c) => s + Number(c.clicks || 0), 0);
                      const totalViews = campaigns.reduce((s, c) => s + Number(c.views || 0), 0);
                      value = totalViews > 0 ? (totalClicks / totalViews * 100) : 0;
                    } else if (h.key === 'cart_conversion') {
                      const totalToCart = campaigns.reduce((s, c) => s + Number(c.to_cart || 0), 0);
                      const totalClicks = campaigns.reduce((s, c) => s + Number(c.clicks || 0), 0);
                      value = totalClicks > 0 ? (totalToCart / totalClicks * 100) : 0;
                    } else if (h.key === 'avg_cpc') {
                      const cpcCamps = campaigns.filter(c => !isCpoCampaign(c));
                      const totalSpent = cpcCamps.reduce((s, c) => s + Number(c.money_spent || 0), 0);
                      const totalClicks = cpcCamps.reduce((s, c) => s + Number(c.clicks || 0), 0);
                      value = totalClicks > 0 ? (totalSpent / totalClicks) : 0;
                    } else if (h.key === 'order_conversion') {
                      const cpcCamps = campaigns.filter(c => !isCpoCampaign(c));
                      const totalOrders = cpcCamps.reduce((s, c) => s + Number(c.orders_count || 0) + Number(c.models_count || 0), 0);
                      const totalClicks = cpcCamps.reduce((s, c) => s + Number(c.clicks || 0), 0);
                      value = totalClicks > 0 ? (totalOrders / totalClicks * 100) : 0;
                    } else if (h.key === 'drr') {
                      const totalSpent = campaigns.reduce((s, c) => s + Number(c.money_spent || 0), 0);
                      const totalRevenue = campaigns.reduce((s, c) => s + Number(c.orders_money || 0) + Number(c.models_money || 0), 0);
                      value = totalRevenue > 0 ? (totalSpent / totalRevenue * 100) : 0;
                    } else {
                      value = campaigns.reduce((s, c) => s + Number(c[h.key] || 0), 0);
                    }
                    return (
                      <TableCell key={h.key} sx={{ ...cellStyle, fontWeight: 'bold' }} align={h.align}>
                        {h.format ? h.format(value) : fmtNum(value)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Note Edit Dialog */}
      <Dialog
        open={noteDialog.open}
        onClose={() => setNoteDialog({ open: false, campaign_id: null, sku: null, stat_date: null, note: '', saving: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Заметка на {noteDialog.stat_date}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder="Введите заметку..."
            value={noteDialog.note}
            onChange={(e) => setNoteDialog(prev => ({ ...prev, note: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, campaign_id: null, sku: null, stat_date: null, note: '', saving: false })}>
            Отмена
          </Button>
          <Button
            onClick={saveNote}
            variant="contained"
            disabled={noteDialog.saving}
            startIcon={noteDialog.saving ? <CircularProgress size={16} /> : null}
          >
            {noteDialog.saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
